import { Alert } from '@/global/components/Alert'
import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Funcionario,
    clientes,
    empresas as empresas_api,
    funcionario,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { funcionarioSchema, semCentroCustoVazio } from '../../constants/schema'
import { AdicionaisForm } from '../forms/Adicionais'
import { CentroCustoForm } from '../forms/CentroCusto'
import { DadosPessoaisForm } from '../forms/DadosPessoais'

export default function AdicionarFuncionarioPage() {
    const navigate = useNavigate()
    const [rascunho] = React.useState(() => getRascunho())
    const empresaId = useStore(state => state.companyId)
    const { data: empresas } = empresas_api.useFetch({ enabled: empresaId > 0 })
    const empresaUsaBancoHoras =
        (empresas?.find(empresa => empresa.id === empresaId)
            ?.periodoFechamentoPonto || 0) > 0
    const { data: limites } = clientes.limites.useFetch(false)
    const podeCadastrar = Number(limites?.ativos) < Number(limites?.limite)

    const { mutateAsync, isLoading } = funcionario.mutations.useCreate({
        invalidateQuery: true,
        onSuccess: () => {
            toast.success('Funcionario adicionado com sucesso!')
        },
        onError: err => {
            toast.error(err.message || 'Erro ao adicionar funcionário')
        },
    })

    useEffect(() => {
        if (Object.values(rascunho).length) {
            localStorage.removeItem('@rascunho:funcionario')
        }
    }, [rascunho])

    return (
        <TabbedDialog.Root
            tabs={[
                {
                    name: 'dados',
                    label: 'Dados básicos',
                    content: <DadosPessoaisForm />,
                },
                {
                    name: 'adicionais',
                    label: 'Adicionais',
                    content: <AdicionaisForm />,
                },
                {
                    name: 'centro-custo',
                    label: 'Centro de Custo',
                    content: <CentroCustoForm />,
                },
            ]}
            open={hasPermission(['create', 'Funcionários'])}
            onSubmit={data => mutateAsync(semCentroCustoVazio(data))}
            loading={isLoading}
            schema={funcionarioSchema}
            defaultValues={{
                ...rascunho,
                empresaId,
                empresaUsaBancoHoras,
                controlarBancoHoras: null,
                possuiEntradaDiaSeguinte: !!rascunho.entradaDiaSeguinte,
                pontoDiferenciado: 'NORMAL',
            }}
            onClose={() => navigate('/cadastros/funcionarios')}
        >
            <TabbedDialog.Title>Adicionar funcionário</TabbedDialog.Title>
            <TabbedDialog.Content>
                {!podeCadastrar && (
                    <Alert severity="warning" className="mb-4">
                        Você atingiu o limite de funcionários ativos. Para
                        adicionar mais,&nbsp;
                        <Link to="/planos/me">altere seu plano</Link>.
                    </Alert>
                )}
            </TabbedDialog.Content>
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
                <TabbedDialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!podeCadastrar}
                />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}

/**
 * Atualiza o rascunho do funcionário no localStorage
 * @param data informações a serem atualizadas no rascunho
 * @param force caso não exista um rascunho, um novo é criado com as informações passadas
 */
export function updateRascunho(
    data: Partial<Funcionario & { tabelaHorarioId?: number }>,
    force = false,
) {
    const rascunho = getRascunho()
    if (!force && !Object.values(rascunho).length) return
    localStorage.setItem(
        '@rascunho:funcionario',
        JSON.stringify({ ...rascunho, ...data }),
    )
}

type RascunhoFuncionario = Partial<Funcionario & { tabelaHorarioId: number }>
/**
 * Retorna o rascunho do funcionário no localStorage
 * @param fallback valor padrão caso não exista um rascunho
 */
function getRascunho(fallback: RascunhoFuncionario = {}): RascunhoFuncionario {
    const rascunho = localStorage.getItem('@rascunho:funcionario')
    return rascunho ? JSON.parse(rascunho) : fallback
}

export { getRascunho as getRascunhoFuncionario }
